/**
 * TestLauncher — page « Labo » (Batch #14.171 → vidé Batch #14.323).
 * Les démos d'idées et la section « à évaluer » ont été retirées ; la page accueille maintenant le tableau d'idées
 * (améliorations proposées Paie · Tenue de livres · Cabinet), sans démo. Filtre par domaine et par priorité.
 */
import { useMemo, useState } from "react";
import { CheckCircle2, FlaskConical, Lightbulb, Sparkles } from "lucide-react";
import { EFFORT_LABEL, IDEAS, IDEA_AREAS, PRIORITY_LABEL } from "@/constants/ideas";
import { TONE_BG } from "@/constants/tones";

const P_CLS = { P0: "bg-rose-100 text-rose-700 border-rose-200", P1: "bg-amber-100 text-amber-800 border-amber-200", P2: "bg-slate-100 text-slate-600 border-slate-200" };

function IdeaCard({ idea, area }) {
  return (
    <div className={`bg-gradient-to-br ${TONE_BG[area.tone]} border rounded-2xl p-4 flex flex-col gap-2`} data-testid={`idea-${idea.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center flex-shrink-0"><idea.icon className="w-5 h-5" /></div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-slate-900 leading-snug">{idea.title}</h3>
          <p className="text-xs text-slate-700 mt-1">{idea.desc}</p>
        </div>
      </div>
      {idea.exists && (
        <div className="flex items-start gap-1.5 text-[11px] text-slate-700 bg-white/70 border border-white rounded-lg px-2.5 py-1.5" data-testid={`idea-exists-${idea.id}`}>
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0 mt-0.5" /><span>{idea.exists}</span>
        </div>
      )}
      {idea.example && (
        <div className="text-[11px] text-slate-800 bg-white/80 border border-white rounded-lg px-2.5 py-1.5" data-testid={`idea-example-${idea.id}`}>
          <span className="font-bold uppercase tracking-wide text-[10px] text-slate-500">Mini exemple</span>
          <p className="mt-0.5">{idea.example}</p>
        </div>
      )}
      {idea.benefit && (
        <div className="flex items-start gap-1.5 text-[11px] text-slate-800 bg-white/80 border border-white rounded-lg px-2.5 py-1.5" data-testid={`idea-benefit-${idea.id}`}>
          <Sparkles className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" /><span><span className="font-bold">Ce que ça vous apporte : </span>{idea.benefit}</span>
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap text-[10px] font-semibold">
        <span className={`px-2 py-0.5 rounded-full border ${P_CLS[idea.p]}`} data-testid={`idea-priority-${idea.id}`}>{idea.p} · {PRIORITY_LABEL[idea.p]}</span>
        <span className="px-2 py-0.5 rounded-full border bg-white/70 border-white text-slate-600">Effort {EFFORT_LABEL[idea.effort]}</span>
        <span className="px-2 py-0.5 rounded-full border bg-white/70 border-white text-slate-600">{area.label}</span>
        {idea.exists && <span className="px-2 py-0.5 rounded-full border bg-emerald-50 border-emerald-200 text-emerald-700">Déjà en partie</span>}
        {idea.example && <span className="px-2 py-0.5 rounded-full border bg-indigo-50 border-indigo-200 text-indigo-700">Retenue</span>}
      </div>
    </div>
  );
}

export default function TestLauncher() {
  const [area, setArea] = useState("all");
  const [prio, setPrio] = useState("all");
  const list = useMemo(() => IDEAS.filter((i) => (area === "all" || i.area === area) && (prio === "all" || i.p === prio)), [area, prio]);
  const areaOf = (id) => IDEA_AREAS.find((a) => a.id === id);

  return (
    <div className="space-y-6 p-4 sm:p-6 max-w-6xl mx-auto" data-testid="test-launcher">
      <div className="bg-gradient-to-r from-indigo-50 via-indigo-50 to-white border-2 border-indigo-200 rounded-2xl p-5 sm:p-6 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-indigo-600 text-white flex items-center justify-center shadow"><FlaskConical className="w-6 h-6" /></div>
          <div>
            <h1 className="text-lg sm:text-xl font-bold text-slate-900">Labo — idées d'amélioration</h1>
            <p className="text-xs text-slate-600 mt-0.5">{IDEAS.length} pistes pour la paie, la tenue de livres et le cabinet. Aucune démo : choisissez-en une et demandez-la pour qu'elle soit construite.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap" data-testid="ideas-filters">
        <button onClick={() => setArea("all")} className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${area === "all" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`} data-testid="ideas-area-all">Tout</button>
        {IDEA_AREAS.map((a) => (
          <button key={a.id} onClick={() => setArea(a.id)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition ${area === a.id ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`} data-testid={`ideas-area-${a.id}`}>
            <a.icon className="w-3.5 h-3.5" /> {a.label}
          </button>
        ))}
        <span className="mx-1 text-slate-300">|</span>
        {["all", "P0", "P1", "P2"].map((p) => (
          <button key={p} onClick={() => setPrio(p)} className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${prio === p ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`} data-testid={`ideas-prio-${p}`}>
            {p === "all" ? "Toutes priorités" : `${p} · ${PRIORITY_LABEL[p]}`}
          </button>
        ))}
        <span className="ml-auto text-xs text-slate-500" data-testid="ideas-count">{list.length} idée(s)</span>
      </div>

      {list.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center text-sm text-slate-500" data-testid="ideas-empty"><Lightbulb className="w-8 h-8 text-slate-300 mx-auto mb-2" />Aucune idée pour ce filtre.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="ideas-grid">
          {list.map((i) => <IdeaCard key={i.id} idea={i} area={areaOf(i.area)} />)}
        </div>
      )}
    </div>
  );
}
